/**
 * oidcStub.js — stub OIDC local para Fase 0.
 *
 * Simula um IdP OIDC (Keycloak/Cognito) localmente.
 * A interface é idêntica à que será usada com o IdP real:
 *   - buildAuthorizationUrl(codeChallenge, state) → URL de autorização
 *   - exchangeCode(code, codeVerifier)             → TokenResponse
 *   - refreshTokens(refreshToken)                  → TokenResponse
 *   - introspect(token)                            → claims
 *
 * Para migrar para o IdP real, implemente `oidcClient.js` com a mesma
 * interface e substitua o import em AuthProvider — sem tocar nas telas.
 *
 * Formato do JWT alinhado com o backend FastAPI (F0-BE-1):
 *   claims obrigatórios: sub, tenant_id, matrix_id, role, name, email, exp, iat, iss
 *
 * Usuários demo mapeados de 04-rbac-e-seguranca.md.
 */

import { generatePkcePair, generateState } from './pkce';
import { hmacSha256 } from './sha256';

// ─── Configuração OIDC (lida do env Vite) ───────────────────────────────────
// Em produção: apontar para o Keycloak/Cognito real.
const OIDC_CONFIG = {
  issuer:        import.meta.env.VITE_OIDC_ISSUER        ?? 'https://stub.nava.local',
  clientId:      import.meta.env.VITE_OIDC_CLIENT_ID     ?? 'nava-flow-web',
  redirectUri:   import.meta.env.VITE_OIDC_REDIRECT_URI  ?? `${window.location.origin}/auth/callback`,
  scope:         'openid profile email',
};

// ─── Config do token stub (DEVE espelhar o backend FastAPI em Fase 0) ────────
// O backend (StubOidcAdapter, HS256) valida assinatura + iss + aud. Para que o
// access_token do stub seja ACEITO pela API real, assinamos um JWT HS256 de
// verdade com o MESMO segredo simétrico e os MESMOS iss/aud efetivos do backend.
//   Backend Fase 0 (services/shared/.../config/settings.py + .env):
//     oidc_stub_secret = 'dev-only-not-for-production-change-me'
//     oidc_issuer / oidc_audience → '' (vazios no .env atual)
// ⚠️ SOMENTE DEV: o segredo simétrico fica no bundle. Em produção o backend
//    recusa o stub (is_production && oidc_stub_mode) e o IdP real (RS256/JWKS)
//    assume — este gerador nunca roda em prod.
const TOKEN_SECRET = import.meta.env.VITE_OIDC_STUB_SECRET ?? 'dev-only-not-for-production-change-me';
const TOKEN_ISS    = import.meta.env.VITE_OIDC_TOKEN_ISS   ?? '';
const TOKEN_AUD    = import.meta.env.VITE_OIDC_TOKEN_AUD   ?? '';

// ─── Usuários do stub (espelho de 04-rbac-e-seguranca.md) ───────────────────
const STUB_USERS = [
  {
    email:     'carlos.oliveira@nava.com.br',
    password:  'Nava@2025!',
    sub:       'a0000000-0001-4000-8000-000000000001',
    name:      'Carlos Oliveira',
    role:      'Tech Lead',
    matrix_id: 'm-admin',
    tenant_id: 'a0000000-0000-4000-8000-000000000001',
    mfa:       true,
  },
  {
    email:     'gabriel.costa@nava.com.br',
    password:  '100432Aede@@',
    sub:       'a0000000-0001-4000-8000-000000000005',
    name:      'Gabriel Costa',
    role:      'Tech Lead',
    matrix_id: 'm-admin',
    tenant_id: 'a0000000-0000-4000-8000-000000000001',
    mfa:       true,
  },
  {
    email:     'crescera@nava.com.br',
    password:  'Senha@2026',
    sub:       'a0000000-0001-4000-8000-000000000008',
    name:      'Crescera',
    role:      'Administrador',
    matrix_id: 'm-admin',
    tenant_id: 'a0000000-0000-4000-8000-000000000001',
    mfa:       true,
  },
  {
    email:     'maria.souza@nava.com.br',
    password:  'Nava@2025!',
    sub:       'a0000000-0001-4000-8000-000000000002',
    name:      'Maria Souza',
    role:      'Product Owner',
    matrix_id: 'm-manager',
    tenant_id: 'a0000000-0000-4000-8000-000000000001',
    mfa:       false,
  },
  {
    email:     'joao.mendes@nava.com.br',
    password:  'Nava@2025!',
    sub:       'a0000000-0001-4000-8000-000000000003',
    name:      'João Mendes',
    role:      'Product Owner',
    matrix_id: 'm-manager',
    tenant_id: 'a0000000-0000-4000-8000-000000000001',
    mfa:       false,
  },
  {
    email:     'ana.lima@nava.com.br',
    password:  'Nava@2025!',
    sub:       'a0000000-0001-4000-8000-000000000004',
    name:      'Ana Lima',
    role:      'Desenvolvedora',
    matrix_id: 'm-analyze',
    tenant_id: 'a0000000-0000-4000-8000-000000000001',
    mfa:       false,
  },
  {
    email:     'rafael.torres@nava.com.br',
    password:  'Nava@2025!',
    sub:       'a0000000-0001-4000-8000-000000000006',
    name:      'Rafael Torres',
    role:      'Analista de Custos',
    matrix_id: 'm-estimative',
    tenant_id: 'a0000000-0000-4000-8000-000000000001',
    mfa:       false,
  },
  {
    email:     'beatriz.ramos@nava.com.br',
    password:  'Nava@2025!',
    sub:       'a0000000-0001-4000-8000-000000000007',
    name:      'Beatriz Ramos',
    role:      'Auditora',
    matrix_id: 'm-viewer',
    tenant_id: 'a0000000-0000-4000-8000-000000000001',
    mfa:       false,
  },
];

// ─── Gerador de JWT stub ─────────────────────────────────────────────────────

// ─── Helpers base64url + assinatura HMAC-SHA256 (WebCrypto) ──────────────────

/** Converte bytes → base64url (sem padding), como exige o JWT. */
function bytesToBase64url(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Codifica uma string UTF-8 em base64url (robusto a acentos: João, etc.). */
function strToBase64url(str) {
  return bytesToBase64url(new TextEncoder().encode(str));
}

/**
 * Assina `message` com HMAC-SHA256 e retorna a assinatura em base64url.
 *
 * Usa WebCrypto quando disponível (contexto seguro: HTTPS/localhost). Ao acessar
 * por IP externo em HTTP puro, `crypto.subtle` é undefined — cai no fallback em
 * JS puro ([[sha256]]), que produz exatamente o mesmo HMAC. Sem isso, o login
 * falharia ("abre a página mas não loga") fora de localhost/HTTPS.
 */
async function hmacSha256Base64url(secret, message) {
  const keyBytes = new TextEncoder().encode(secret);
  const msgBytes = new TextEncoder().encode(message);

  if (typeof crypto !== 'undefined' && crypto.subtle && crypto.subtle.sign) {
    try {
      const key = await crypto.subtle.importKey(
        'raw', keyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'],
      );
      const sig = await crypto.subtle.sign('HMAC', key, msgBytes);
      return bytesToBase64url(new Uint8Array(sig));
    } catch {
      /* contexto inseguro / subtle indisponível → fallback abaixo */
    }
  }

  return bytesToBase64url(hmacSha256(keyBytes, msgBytes));
}

/**
 * Cria um JWT HS256 assinado de verdade com o segredo simétrico compartilhado
 * (TOKEN_SECRET), aceito pelo StubOidcAdapter do backend em Fase 0.
 *
 * iss/aud espelham a config efetiva do backend (TOKEN_ISS/TOKEN_AUD). Quando o
 * IdP real (RS256/JWKS) entrar, este gerador é substituído pelo token do provider.
 *
 * @param {object} claims  — deve conter sub (UUID), tenant_id (UUID), matrix_id
 * @param {number} expiresInSeconds
 * @returns {Promise<string>} JWT (header.payload.signature) válido para a API
 */
async function createStubJwt(claims, expiresInSeconds = 3600) {
  const now = Math.floor(Date.now() / 1000);
  const payload = {
    ...claims,
    iss: TOKEN_ISS,
    aud: TOKEN_AUD,
    iat: now,
    exp: now + expiresInSeconds,
  };

  const header        = strToBase64url(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const body          = strToBase64url(JSON.stringify(payload));
  const signingInput  = `${header}.${body}`;
  const sig           = await hmacSha256Base64url(TOKEN_SECRET, signingInput);
  return `${signingInput}.${sig}`;
}

// ─── Interface pública ───────────────────────────────────────────────────────

/**
 * buildAuthorizationUrl — gera a URL de redirecionamento para o IdP.
 * No stub, a "URL" é apenas retornada; a tela de login usa autenticação
 * direta (email/senha) em vez de redirecionar para um IdP externo.
 *
 * @param {string} codeChallenge
 * @param {string} state
 * @returns {string}
 */
export function buildAuthorizationUrl(codeChallenge, state) {
  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             OIDC_CONFIG.clientId,
    redirect_uri:          OIDC_CONFIG.redirectUri,
    scope:                 OIDC_CONFIG.scope,
    state,
    code_challenge:        codeChallenge,
    code_challenge_method: 'S256',
  });
  return `${OIDC_CONFIG.issuer}/authorize?${params}`;
}

/**
 * authenticateCredentials — equivalente à troca de credenciais no stub.
 * No fluxo OIDC real, o código é gerado pelo IdP após autenticação na
 * página do provedor. No stub, simulamos isso aqui.
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{ user: object, authCode: string }>}
 * @throws {Error} com message i18n-key se credenciais inválidas
 */
export async function authenticateCredentials(email, password) {
  // Simula latência de rede
  await new Promise((r) => setTimeout(r, 400));

  const user = STUB_USERS.find(
    (u) => u.email.toLowerCase() === email.trim().toLowerCase() && u.password === password,
  );

  if (!user) {
    throw Object.assign(new Error('INVALID_CREDENTIALS'), { status: 401 });
  }

  // Remove campos sensíveis antes de devolver ao chamador
  const { password: _pwd, ...safeUser } = user;

  // Gera código de autorização one-use (stub)
  const authCode = `stub_code_${user.sub}_${Date.now()}`;
  // Armazena temporariamente para simulação de exchange (inclui mfa, excluída de safeUser)
  sessionStorage.setItem(`_stub_code_${authCode}`, JSON.stringify(user));

  return { user: safeUser, authCode };
}

/**
 * exchangeCode — troca o authorization code por tokens.
 * Alinhado com o token_endpoint do OIDC padrão.
 *
 * @param {string} code
 * @param {string} _codeVerifier  (ignorado no stub; validado no IdP real)
 * @returns {Promise<TokenResponse>}
 */
export async function exchangeCode(code, _codeVerifier) {
  await new Promise((r) => setTimeout(r, 200));

  const userJson = sessionStorage.getItem(`_stub_code_${code}`);
  if (!userJson) {
    throw Object.assign(new Error('INVALID_CODE'), { status: 400 });
  }

  const user = JSON.parse(userJson);
  sessionStorage.removeItem(`_stub_code_${code}`);

  const { codeVerifier: _cv, password: _p, mfa, ...claims } = user;

  const accessToken  = await createStubJwt(claims, 3600);
  const refreshToken = await createStubJwt({ sub: user.sub, type: 'refresh' }, 86400);
  const idToken      = await createStubJwt({
    ...claims,
    nonce: 'stub-nonce',
  }, 3600);

  return {
    access_token:  accessToken,
    refresh_token: refreshToken,
    id_token:      idToken,
    token_type:    'Bearer',
    expires_in:    3600,
    requiresMfa:   mfa,
  };
}

/**
 * refreshTokens — renova o access token usando o refresh token.
 *
 * @param {string} refreshToken
 * @returns {Promise<TokenResponse>}
 */
export async function refreshTokens(refreshToken) {
  await new Promise((r) => setTimeout(r, 200));

  if (!refreshToken || !refreshToken.startsWith('ey')) {
    throw Object.assign(new Error('INVALID_REFRESH_TOKEN'), { status: 401 });
  }

  // Decodifica sub do refresh token stub
  try {
    const parts = refreshToken.split('.');
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const user = STUB_USERS.find((u) => u.sub === payload.sub);
    if (!user) throw new Error('USER_NOT_FOUND');

    const { password: _p, mfa, ...claims } = user;
    const newAccess  = await createStubJwt(claims, 3600);
    const newRefresh = await createStubJwt({ sub: user.sub, type: 'refresh' }, 86400);

    return {
      access_token:  newAccess,
      refresh_token: newRefresh,
      id_token:      null,
      token_type:    'Bearer',
      expires_in:    3600,
      requiresMfa:   false,
    };
  } catch {
    throw Object.assign(new Error('INVALID_REFRESH_TOKEN'), { status: 401 });
  }
}

/**
 * Retorna PKCE pair e state para iniciar um fluxo de redirect (SSO).
 * Usado pelo botão "Entrar com SSO" na LoginScreen.
 */
export async function initiateSsoFlow() {
  const { codeVerifier, codeChallenge } = await generatePkcePair();
  const state = generateState();
  // Persiste o verifier para validação no callback
  sessionStorage.setItem('pkce_verifier', codeVerifier);
  sessionStorage.setItem('oidc_state', state);
  const url = buildAuthorizationUrl(codeChallenge, state);
  return { url, state };
}

/**
 * Verifica o código MFA stub.
 * Aceita qualquer código de 6 dígitos no stub — troca por TOTP real na Fase 2.
 *
 * @param {string} code
 * @returns {Promise<boolean>}
 */
export async function verifyMfaCode(code) {
  await new Promise((r) => setTimeout(r, 300));
  // Stub: aceita qualquer código de 6 dígitos
  return /^\d{6}$/.test(code.trim());
}
